import { Employee, PurchaseOrderStatusEnum } from "@finway-group/shared/lib/models"
import { PurchaseOrderActions } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatusAction.enum"
import { Card } from "antd"
import { TFunction } from "i18next"
import React from "react"
import { X as CancelIcon, Check as CheckIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useLoggedInEmployeeProfile } from "Shared/queries/employee.queries"
import { AuthzService } from "Shared/services"
import { insertIf } from "Shared/utils/helper.utils"
import { PurchaseOrder } from "../types/PurchaseOrder.interfaces"
import { isCardMissing } from "../types/utils/helper.util"
import { PurchaseOrderActionButton } from "./PurchaseOrderActionButton"

// Extract the props type from PurchaseOrderActionButton, excluding the ones we'll provide
type ButtonConfigProps = Omit<React.ComponentProps<typeof PurchaseOrderActionButton>, "purchaseOrderId">

interface PurchaseOrderActionButtonsProps {
    purchaseOrder: PurchaseOrder
    isDisabled: boolean
    children?: React.ReactNode
}

interface StateConfig {
    title: string
    subtitle: string
    buttons: Array<ButtonConfigProps>
}

export const getStateConfig = (purchaseOrder: PurchaseOrder, user: Employee | null | undefined, isDisabled: boolean, t: TFunction): StateConfig | null => {
    const { availableActions } = purchaseOrder.metadata

    const isApprover = user?.id === purchaseOrder.approver._id
    const buttonTextNext = t(`purchaseOrders:actionButtons.${availableActions?.next}`)
    const buttonTextPrevious = t(`purchaseOrders:actionButtonsPrevious.${availableActions?.previous}`)
    const buttonTextReject = t(`purchaseOrders:actionButtonsReject.${availableActions?.reject}`)

    const shouldShowApproveButton = AuthzService.canLoggedInUserApprovePurchaseOrder(purchaseOrder)

    switch (purchaseOrder.state.currentState) {
        case PurchaseOrderStatusEnum.DRAFT:
            return {
                title: t(`purchaseOrders:actionTitles.${PurchaseOrderStatusEnum.DRAFT}`),
                subtitle: t(`purchaseOrders:actionSubtitles.${PurchaseOrderStatusEnum.DRAFT}`),
                buttons: [
                    {
                        label: buttonTextNext,
                        purchaseOrderAction: availableActions?.next,
                        type: "primary",
                        icon: <CheckIcon />,
                        isDisabled,
                    },
                ],
            }

        case PurchaseOrderStatusEnum.WAITING_FOR_APPROVAL:
            return {
                title: t(`purchaseOrders:actionTitles.${PurchaseOrderStatusEnum.WAITING_FOR_APPROVAL}${isApprover ? "" : "__not_approver"}`),
                subtitle: t(`purchaseOrders:actionSubtitles.${PurchaseOrderStatusEnum.WAITING_FOR_APPROVAL}${isApprover ? "" : "__not_approver"}`),
                buttons: shouldShowApproveButton
                    ? [
                          {
                              label: buttonTextReject,
                              purchaseOrderAction: availableActions?.reject,
                              color: "danger",
                              icon: <CancelIcon />,
                              isDisabled,
                              rejectConfirmation: {
                                  title: t("purchaseOrders:rejection_note.title"),
                                  message: t("purchaseOrders:rejection_note.message"),
                                  okText: t("purchaseOrders:rejection_note.confirm"),
                                  cancelText: t("purchaseOrders:rejection_note.cancel"),
                              },
                          },
                          {
                              label: buttonTextNext,
                              purchaseOrderAction: availableActions?.next,
                              type: "primary",
                              icon: <CheckIcon />,
                              isDisabled,
                          },
                          {
                              label: buttonTextPrevious,
                              purchaseOrderAction: availableActions?.previous,
                              color: "warning",
                              isDisabled,
                          },
                      ]
                    : [],
            }

        case PurchaseOrderStatusEnum.PROCUREMENT:
            return {
                title: t(`purchaseOrders:actionTitles.${PurchaseOrderStatusEnum.PROCUREMENT}`),
                subtitle: t(`purchaseOrders:actionSubtitles.${PurchaseOrderStatusEnum.PROCUREMENT}`),
                buttons: AuthzService.isUserProcurementApprover()
                    ? [
                          {
                              label: buttonTextPrevious,
                              purchaseOrderAction: availableActions?.previous,
                              color: "danger",
                              icon: <CancelIcon />,
                              isDisabled,
                          },
                          {
                              label: buttonTextNext,
                              purchaseOrderAction: availableActions?.next,
                              type: "primary",
                              icon: <CheckIcon />,
                              isDisabled,
                          },
                      ]
                    : [],
            }

        case PurchaseOrderStatusEnum.FULFILLMENT:
            return {
                title: t(`purchaseOrders:actionTitles.${PurchaseOrderStatusEnum.FULFILLMENT}`),
                subtitle: t(`purchaseOrders:actionSubtitles.${PurchaseOrderStatusEnum.FULFILLMENT}`),
                buttons: [
                    ...(isCardMissing(purchaseOrder)
                        ? [
                              {
                                  label: t(`purchaseOrders:actionButtons.${PurchaseOrderActions.CREATE_CARD}`),
                                  purchaseOrderAction: PurchaseOrderActions.CREATE_CARD,
                                  type: "primary" as ButtonConfigProps["type"],
                                  color: "warning",
                                  isDisabled: !user ? true : !user?.isEnrolledInCardProgramOfCompany({ companyId: user?.activeCompanyProfile.companyId }),
                              },
                          ]
                        : [
                              ...insertIf(shouldShowApproveButton, {
                                  label: t(`purchaseOrders:actionButtons.${PurchaseOrderActions.CLOSE_PURCHASE_ORDER}`),
                                  type: "primary" as ButtonConfigProps["type"],
                                  color: "warning",
                                  purchaseOrderAction: PurchaseOrderActions.CLOSE_PURCHASE_ORDER,
                                  isDisabled: false,
                              }),
                          ]),
                ],
            }

        default:
            return null
    }
}

export const PurchaseOrderActionButtons = ({ purchaseOrder, isDisabled, children }: PurchaseOrderActionButtonsProps) => {
    const { t } = useTranslation()
    const user = useLoggedInEmployeeProfile()
    const stateConfig = getStateConfig(purchaseOrder, user, isDisabled, t)
    if (!stateConfig && !children) {
        return null
    }
    if (!stateConfig) {
        return <Card>{children}</Card>
    }

    const hasTitle = stateConfig.title || stateConfig.subtitle
    const isMultipleButtons = stateConfig.buttons.length > 1

    return (
        <Card>
            {hasTitle && (
                <div className="mb-10">
                    {stateConfig.title && <h2 className="mb-0 pl-0 -mt-3">{stateConfig.title}</h2>}
                    {stateConfig.subtitle && <p className="mb-5">{stateConfig.subtitle}</p>}
                </div>
            )}
            {stateConfig.buttons.length > 0 && (
                <div className={isMultipleButtons ? "grid grid-cols-2 gap-8" : ""}>
                    {stateConfig.buttons.map((button, index) => (
                        <PurchaseOrderActionButton
                            className={`mb-10 ${isMultipleButtons && index === 2 ? "col-span-2" : "flex-1 w-full"}`}
                            key={index}
                            label={button.label}
                            purchaseOrderId={purchaseOrder._id}
                            purchaseOrderAction={button.purchaseOrderAction}
                            isDisabled={button.isDisabled}
                            type={button.type}
                            color={button.color}
                            icon={button.icon}
                            rejectConfirmation={button.rejectConfirmation}
                        />
                    ))}
                </div>
            )}
            {children}
        </Card>
    )
}
